import { useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { useAppDispatch } from '../../../hooks';
import { filterByDate } from '../../../helpers/filterObject';
import {
    setActionsToTrigger,
    TAction,
} from '../../../state/ui/discovery/snapshotting';
import TranslationHelper from '../../../helpers/TranslationHelper';
import { selectObject } from '../../../state/_actions';
import { IPreviewAction } from '../../../state/ui/discovery/types';
import { ISourceSet } from '../../../state/types';

export function useHandleTriggerActions({
    sourceSet,
    creatorLevel,
    previewAction,
    actionsToTrigger,
    dispatch,
}: {
    sourceSet: ISourceSet | null;
    creatorLevel: number;
    previewAction: IPreviewAction | null;
    actionsToTrigger: TAction[] | undefined;
    dispatch: ReturnType<typeof useAppDispatch>;
}) {
    const { enqueueSnackbar } = useSnackbar();

    const showEventTriggerAction = actionsToTrigger?.find(
        (action) => action.type === 'showEvent'
    );
    const showFrameTriggerAction = actionsToTrigger?.find(
        (action) => action.type === 'showFrame'
    );

    const handleTriggerAction = useCallback(
        (
            triggerAction: typeof showEventTriggerAction,
            type: 'event' | 'frame'
        ) => {
            if (!sourceSet || !triggerAction?.date) return;

            const event = filterByDate(
                sourceSet.entities,
                triggerAction.date,
                'updatedAt'
            );
            dispatch(setActionsToTrigger(undefined, creatorLevel));

            if (!event) {
                enqueueSnackbar(
                    TranslationHelper.translate('Event not found'),
                    {
                        variant: 'error',
                    }
                );
                return;
            }

            if (type === 'event') {
                const eventId = event.id || '';
                dispatch(
                    selectObject(
                        sourceSet,
                        {
                            id: eventId,
                            _meta: {
                                actions: {
                                    preview: {
                                        params: {
                                            ...previewAction?.params,
                                            id: eventId,
                                        },
                                        api: '',
                                    },
                                },
                            },
                        },
                        creatorLevel,
                        false
                    )
                );
            } else {
                dispatch(selectObject(sourceSet, event, creatorLevel, false));
            }
        },
        [dispatch, sourceSet, creatorLevel, previewAction, enqueueSnackbar]
    );

    useEffect(() => {
        handleTriggerAction(showEventTriggerAction, 'event');
    }, [showEventTriggerAction, sourceSet, handleTriggerAction]);

    useEffect(() => {
        handleTriggerAction(showFrameTriggerAction, 'frame');
    }, [showFrameTriggerAction, sourceSet, handleTriggerAction]);
}
